import { splitName } from '../core/documents';
import { MAX_WINDOWS } from '../core/geometry';
import { activeDoc, focusedWindow, windowOf } from '../core/session';
import { setBlockStyle, toggleInline, toggleList, toggleQuote, type BlockStyle } from '../editor/markdownCommands';
import { runSpec } from '../editor/setup';
import { savePreference, type ThemePreference } from '../platform/theme';
import type { Controller } from './controller';
import { openAccount, patchUi, setRendered, ui } from './ui';
import { notify } from './ui';

/**
 * Everything the command palette can do, in one short list. Commands are
 * built fresh for the current situation, so a command that cannot apply is
 * simply absent rather than greyed out.
 */

export type CommandGroup = 'Document' | 'Cloud' | 'Window' | 'View' | 'Format' | 'Go to';

export interface Command {
  id: string;
  label: string;
  group: CommandGroup;
  /** Shortcut spec, e.g. "Mod-s". */
  shortcut?: string;
  /** Extra words that should find this command. */
  keywords?: string;
  /** Secondary text, e.g. where a document lives. */
  detail?: string;
  checked?: boolean;
  run(): void;
}

export const GROUP_ORDER: CommandGroup[] = ['Document', 'Cloud', 'Window', 'View', 'Format', 'Go to'];

function setTheme(id: ThemePreference) {
  savePreference(id);
  patchUi({ theme: id });
}

/** Ask a window to show its active document's card. */
export function openDocumentCard(windowId: string) {
  window.dispatchEvent(new CustomEvent('mytxt:document-card', { detail: { windowId } }));
}

export function buildCommands(app: Controller): Command[] {
  const s = app.session.get();
  const win = focusedWindow(s);
  const doc = activeDoc(s);
  const { prefs, theme } = ui.get();
  const list: Command[] = [];
  const add = (c: Command) => list.push(c);

  add({ id: 'new-md', group: 'Document', label: 'New Markdown', shortcut: 'Mod-n', keywords: 'create document md', run: () => app.newDocument('md') });
  add({ id: 'new-txt', group: 'Document', label: 'New Plain Text', shortcut: 'Mod-Alt-n', keywords: 'create document txt', run: () => app.newDocument('txt') });
  add({ id: 'open', group: 'Document', label: 'Open File', shortcut: 'Mod-o', keywords: 'load import', run: () => app.openFiles() });
  if (doc) {
    add({ id: 'save', group: 'Document', label: 'Save', shortcut: 'Mod-s', keywords: 'write', run: () => app.save(doc.id) });
    add({ id: 'save-as', group: 'Document', label: 'Save As…', shortcut: 'Mod-Shift-s', keywords: 'export download copy', run: () => app.saveAs(doc.id) });
    if (doc.origin !== 'file') add({ id: 'rename', group: 'Document', label: 'Rename…', keywords: 'name title format', run: () => openDocumentCard(win.id) });
    add({ id: 'close', group: 'Document', label: 'Close Document', shortcut: 'Mod-w', keywords: 'tab', run: () => app.close(doc.id) });
  }

  if (app.cloud.configured) {
    const auth = app.cloud.auth.state.get();
    const sync = app.cloud.sync.get();
    const usable = auth.status === 'signed-in' && !!sync && sync.access !== 'denied';
    if (auth.status === 'signed-in') add({ id: 'cloud-account', group: 'Cloud', label: 'Account & Cloud…', keywords: 'library sign out email profile documents', run: () => openAccount() });
    else add({ id: 'cloud-sign-in', group: 'Cloud', label: 'Sign In…', keywords: 'account register create password recover cloud', run: () => openAccount() });
    if (usable) {
      add({ id: 'cloud-sync', group: 'Cloud', label: 'Sync Now', keywords: 'cloud upload refresh', run: () => void app.cloud.syncNow() });
      add({ id: 'cloud-autosave', group: 'Cloud', label: 'Cloud Autosave', keywords: 'sync automatically upload', checked: prefs.cloudAutosave, run: () => app.setPrefs({ cloudAutosave: !prefs.cloudAutosave }) });
      if (doc && !doc.cloud) {
        add({
          id: 'cloud-save',
          group: 'Cloud',
          label: 'Save to Cloud',
          keywords: 'upload move library',
          run: async () => {
            const [report] = await app.cloud.moveToCloud([doc.id]);
            if (report) notify(report.status === 'verified' ? `Saved “${doc.name}” to your cloud library.` : report.detail, report.status === 'failed' ? 'error' : 'info');
          },
        });
      }
      add({ id: 'cloud-library', group: 'Cloud', label: 'Cloud Library…', keywords: 'open documents list', run: () => openAccount() });
    }
  }

  if (s.windows.length < MAX_WINDOWS) add({ id: 'new-window', group: 'Window', label: 'New Window', keywords: 'canvas', run: () => app.newWindow() });
  if (!ui.get().compact) {
    add({ id: 'layout', group: 'Window', label: 'Window Layout…', keywords: 'arrange tile snap half quarter third rectangle', run: () => patchUi({ layoutFor: win.id }) });
    add({ id: 'layout-left', group: 'Window', label: 'Left Half', keywords: 'layout snap', shortcut: 'Ctrl-Alt-ArrowLeft', run: () => app.layoutWindow(win.id, 'left') });
    add({ id: 'layout-right', group: 'Window', label: 'Right Half', keywords: 'layout snap', shortcut: 'Ctrl-Alt-ArrowRight', run: () => app.layoutWindow(win.id, 'right') });
    add({ id: 'layout-center', group: 'Window', label: 'Center Window', keywords: 'layout middle', run: () => app.layoutWindow(win.id, 'center') });
    add({ id: 'layout-default', group: 'Window', label: 'Original Window Size', keywords: 'layout reset restore default centred centered', run: () => app.layoutWindow(win.id, 'default') });
    if (s.windows.length > 1) {
      add({ id: 'tidy', group: 'Window', label: 'Arrange All Windows', keywords: 'tidy grid tile organise organize layout', run: () => app.arrangeWindows('auto') });
      add({ id: 'cascade', group: 'Window', label: 'Cascade Windows', keywords: 'stack overlap layout', run: () => app.arrangeWindows('cascade') });
      add({ id: 'match-size', group: 'Window', label: 'Match Size to This Window', keywords: 'same size equal layout', run: () => app.matchWindowSizes(win.id) });
    }
    if (app.canUndoLayout()) add({ id: 'undo-layout', group: 'Window', label: 'Undo Arrangement', keywords: 'layout back restore', run: () => app.undoLayout() });
    add({ id: 'auto-arrange', group: 'Window', label: 'Auto Arrange', keywords: 'layout automatic tile', checked: prefs.autoArrange, run: () => (app.setPrefs({ autoArrange: !prefs.autoArrange }), !prefs.autoArrange && app.arrangeWindows('auto')) });
  }
  add({ id: 'zoom', group: 'Window', label: win.zone === 'max' ? 'Restore Window' : 'Maximize Window', keywords: 'zoom fill fullscreen size', run: () => app.toggleMaximize(win.id) });
  if (doc && win.docIds.length > 1 && s.windows.length < MAX_WINDOWS) add({ id: 'move-window', group: 'Window', label: 'Move Document to New Window', keywords: 'tear split', run: () => app.moveToNewWindow(doc.id) });
  if (s.windows.length > 1) add({ id: 'close-window', group: 'Window', label: 'Close Window', run: () => app.closeWindow(win.id) });

  const themes: Array<[ThemePreference, string]> = [
    ['light', 'Light Appearance'],
    ['dark', 'Dark Appearance'],
    ['system', 'Match System Appearance'],
  ];
  for (const [id, label] of themes) add({ id: `theme-${id}`, group: 'View', label, keywords: 'theme mode colour color', checked: theme === id, run: () => setTheme(id) });
  add({ id: 'line-numbers', group: 'View', label: 'Line Numbers', keywords: 'gutter', checked: prefs.lineNumbers, run: () => app.setPrefs({ lineNumbers: !prefs.lineNumbers }) });
  add({ id: 'current-line', group: 'View', label: 'Highlight Current Line', keywords: 'caret row band', checked: prefs.currentLine, run: () => app.setPrefs({ currentLine: !prefs.currentLine }) });
  add({ id: 'tab-indent', group: 'View', label: 'Tab Indents Selection', keywords: 'indent tab key focus', checked: prefs.tabIndent, run: () => app.setPrefs({ tabIndent: !prefs.tabIndent }) });
  if (doc?.kind === 'md') {
    const shown = ui.get().rendered.has(doc.id);
    add({ id: 'rendered', group: 'View', label: shown ? 'Show Markdown Source' : 'Show Rendered Markdown', keywords: 'preview render source read', run: () => setRendered(doc.id, !shown) });
  }

  const view = app.view();
  if (doc?.kind === 'md' && view) {
    const run = (make: Parameters<typeof runSpec>[1]) => () => {
      runSpec(view, make);
      view.focus();
    };
    const styles: Array<[BlockStyle, string]> = [
      [1, 'Heading 1'],
      [2, 'Heading 2'],
      [3, 'Heading 3'],
      [0, 'Body Text'],
    ];
    for (const [level, label] of styles) add({ id: `block-${level}`, group: 'Format', label, shortcut: `Mod-Alt-${level}`, keywords: 'style paragraph title', run: run((st) => setBlockStyle(st, level)) });
    add({ id: 'bold', group: 'Format', label: 'Bold', shortcut: 'Mod-b', keywords: 'strong', run: run((st) => toggleInline(st, 'bold')) });
    add({ id: 'italic', group: 'Format', label: 'Italic', shortcut: 'Mod-i', keywords: 'emphasis', run: run((st) => toggleInline(st, 'italic')) });
    add({ id: 'bullets', group: 'Format', label: 'Bulleted List', shortcut: 'Mod-Shift-8', run: run((st) => toggleList(st, 'bullet')) });
    add({ id: 'numbers', group: 'Format', label: 'Numbered List', shortcut: 'Mod-Shift-7', run: run((st) => toggleList(st, 'ordered')) });
    add({ id: 'quote', group: 'Format', label: 'Quote', shortcut: 'Mod-Shift-9', run: run(toggleQuote) });
    add({ id: 'link', group: 'Format', label: 'Link…', keywords: 'url address', run: () => (view.focus(), patchUi({ linkEditor: 'primary', typing: false })) });
  }

  for (const d of s.docs) {
    if (d.id === doc?.id) continue;
    const owner = windowOf(s, d.id);
    const { base, ext } = splitName(d.name);
    add({
      id: `go-${d.id}`,
      group: 'Go to',
      label: base + ext,
      detail: s.windows.length > 1 && owner ? `Window ${s.windows.indexOf(owner) + 1}` : undefined,
      keywords: 'switch document tab',
      run: () => app.activate(d.id),
    });
  }
  return list;
}

/**
 * How well `query` matches `text`: contiguous matches beat scattered ones,
 * matches at word starts beat matches inside words. Null means no match.
 */
export function matchScore(query: string, text: string): number | null {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const t = text.toLowerCase();
  const at = t.indexOf(q);
  if (at >= 0) return 100 - at + (at === 0 || /\W/.test(t[at - 1]) ? 50 : 0) + (t.length === q.length ? 20 : 0);
  // Subsequence: every character in order, rewarding word starts.
  let score = 0;
  let from = 0;
  for (const ch of q) {
    if (ch === ' ') continue;
    const i = t.indexOf(ch, from);
    if (i < 0) return null;
    score += i === 0 || /\W/.test(t[i - 1]) ? 6 : i === from ? 3 : 1;
    from = i + 1;
  }
  return score;
}

export function filterCommands(commands: Command[], query: string): Command[] {
  if (!query.trim()) return commands;
  return commands
    .map((c) => {
      const main = matchScore(query, c.label);
      const extra = matchScore(query, `${c.keywords ?? ''} ${c.group}`);
      const score = main !== null ? main + 10 : extra !== null ? extra * 0.5 : null;
      return { c, score };
    })
    .filter((x): x is { c: Command; score: number } => x.score !== null)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.c);
}
